
import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";

interface StandardDatePickerProps {
  date: string;
  setDate: (date: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
  icon?: React.ReactNode;
}

export function StandardDatePicker({ 
  date, 
  setDate, 
  label = "Date", 
  placeholder = "Select date",
  required = false,
  className,
  icon = <CalendarIcon size={14} className="mr-1 text-accent dark:text-accent" />
}: StandardDatePickerProps) {
  // Convert string date to Date object for the calendar
  const selectedDate = date ? new Date(date) : undefined;
  
  // Handle date selection
  const handleSelect = (newDate: Date | undefined) => {
    if (newDate) {
      // Format as YYYY-MM-DD for form value using local date components
      const year = newDate.getFullYear();
      const month = String(newDate.getMonth() + 1).padStart(2, '0');
      const day = String(newDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      setDate(formattedDate);
    } else {
      setDate('');
    }
  };

  return (
    <div className="space-y-2">
      {label && (
      <Label htmlFor="datePicker" className="text-sm font-medium text-foreground/80 flex items-center">
        {icon}
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      )}
      
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "inline-flex items-center justify-start gap-2 whitespace-nowrap rounded-md text-sm font-medium",
              "ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2", 
              "focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
              "w-full text-left font-normal",
              "bg-white/30 dark:bg-white/10 backdrop-blur-sm border border-accent/20",
              "focus:border-accent/40 focus-visible:border-accent/40 transition-all duration-300 h-10 px-3 py-2",
              "hover:border-accent/30 hover:shadow-md", 
              "shadow-sm",
              !selectedDate && "text-muted-foreground",
              className
            )}
            id="datePicker"
          >
            <CalendarIcon className="mr-2 h-4 w-4 text-accent" />
            {selectedDate ? format(selectedDate, "PPP") : <span>{placeholder}</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md shadow-lg" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            initialFocus
            className="p-3 pointer-events-auto"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
